/**
 * Time-Series Statistics Hook (Phase 2.1 Implementation)
 * Enhanced hook based on Phase 1 database functions for time-series analytics
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSafeUserId } from '@/hooks/useAuthGuard';
import { supabase } from '@/services/supabase';
import { useLanguageStore } from '@/stores/useLanguageStore';

// Time range configuration for Phase 2.1
export interface TimeRange {
  label: string;
  value: '7d' | '30d' | '3m' | 'all';
  days: number;
}

export const TIME_RANGES: TimeRange[] = [
  { label: '7 Days', value: '7d', days: 7 },
  { label: '30 Days', value: '30d', days: 30 },
  { label: '3 Months', value: '3m', days: 90 },
  { label: 'All Time', value: 'all', days: 365 }
];

// Enhanced time-series data interfaces (Phase 2.1)
export interface StudyStreakData {
  current: number;
  longest: number;
  lastActivityDate: string;
}

export interface LearningProgressData {
  totalWords: number;
  newWordsInPeriod: number;
  wordsMasteredInPeriod: number;
  periodAccuracy: number;
  chartData: Array<{ date: string; value: number; }>;
}

// Legacy interface for backwards compatibility
interface TimeSeriesStats {
  study_streak: {
    current: number;
    longest: number;
    last_activity: string | null;
  };
  total_words: {
    current: number;
    mastered: number;
    learning: number;
    due_for_review: number;
  };
  growth: {
    '7d': {
      words_learned: number;
      growth_rate: number;
      study_days: number;
    };
    '30d': {
      words_learned: number;
      growth_rate: number;
      study_days: number;
    };
  };
}

interface CachedData<T> {
  data: T | null;
  timestamp: number;
  loading: boolean;
  error: string | null;
}

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache for dashboard stats
const REALTIME_UPDATE_THRESHOLD = 5 * 1000; // 5 seconds for real-time updates

// Enhanced time-series hook for Phase 2.1 implementation
export const useTimeSeriesData = (timeRange: TimeRange) => {
  const userId = useSafeUserId();
  const { targetLanguage } = useLanguageStore();
  const [studyStreakData, setStudyStreakData] = useState<StudyStreakData>({
    current: 0,
    longest: 0,
    lastActivityDate: new Date().toISOString().split('T')[0]
  });

  const [learningProgressData, setLearningProgressData] = useState<LearningProgressData>({
    totalWords: 0,
    newWordsInPeriod: 0,
    wordsMasteredInPeriod: 0,
    periodAccuracy: 0,
    chartData: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchRef = useRef<number>(0);

  const fetchTimeSeriesData = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch streak, period stats, and chart data in parallel
      const [streakResponse, progressResponse, chartResponse] = await Promise.all([
        supabase.rpc('calculate_study_streak', {
          p_user_id: userId,
          p_language: targetLanguage
        }),
        supabase.rpc('get_learning_progress_by_period', {
          p_user_id: userId,
          p_language: targetLanguage,
          p_period: timeRange.value
        }),
        supabase.rpc('get_learning_progress_chart_data', {
          p_user_id: userId,
          p_language_code: targetLanguage,
          p_days_back: timeRange.days
        })
      ]);

      if (streakResponse.error) {
        throw new Error(`Study streak error: ${streakResponse.error.message}`);
      }

      if (progressResponse.error) {
        throw new Error(`Learning progress error: ${progressResponse.error.message}`);
      }

      // Process streak data
      const streakData = Array.isArray(streakResponse.data) ? streakResponse.data[0] : streakResponse.data;
      setStudyStreakData({
        current: streakData?.current_streak || 0,
        longest: streakData?.longest_streak || 0,
        lastActivityDate: streakData?.last_activity_date || new Date().toISOString().split('T')[0]
      });

      // Process learning progress data
      const progressData = Array.isArray(progressResponse.data) ? progressResponse.data[0] : progressResponse.data;

      // Build chart data from get_learning_progress_chart_data (returns dated rows from learning_history)
      let chartData: Array<{ date: string; value: number; }> = [];

      if (!chartResponse.error && Array.isArray(chartResponse.data) && chartResponse.data.length > 0) {
        chartData = chartResponse.data.map((point: { date: string; total_words_known: number }) => ({
          date: point.date,
          value: point.total_words_known || 0
        }));
      } else if (progressData?.total_words > 0) {
        // Fallback: single data point for today if no historical data
        chartData = [{
          date: new Date().toISOString().split('T')[0],
          value: progressData.total_words
        }];
      }

      const learningData = {
        totalWords: progressData?.total_words || 0,
        newWordsInPeriod: progressData?.new_words_in_period || 0,
        wordsMasteredInPeriod: progressData?.words_mastered_in_period || 0,
        periodAccuracy: Math.round((progressData?.period_accuracy || 0) * 100),
        chartData
      };


      setLearningProgressData(learningData);
      lastFetchRef.current = Date.now();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch time-series data';
      console.error('Error in useTimeSeriesData:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId, timeRange.value, targetLanguage]);

  useEffect(() => {
    fetchTimeSeriesData();
  }, [fetchTimeSeriesData]);

  // Refetch when the tab regains focus so data stays fresh after
  // navigating away (e.g. completing a chat or review session) (#204).
  // Guarded by a 30s stale-time check to prevent a refetch + re-render
  // cascade on every tab switch (mirrors useDailyProgressData).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const staleDuration = 30_000; // 30 seconds, matches useDailyProgressData
        if (Date.now() - lastFetchRef.current > staleDuration) {
          fetchTimeSeriesData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchTimeSeriesData]);

  return {
    // Enhanced Phase 2.1 data
    studyStreakData,
    learningProgressData,

    // Aggregated for convenience
    totalWords: learningProgressData.totalWords,
    newWordsInPeriod: learningProgressData.newWordsInPeriod,
    wordsMasteredInPeriod: learningProgressData.wordsMasteredInPeriod,
    periodAccuracy: learningProgressData.periodAccuracy,
    chartData: learningProgressData.chartData,

    // State management
    isLoading,
    error,
    refetch: fetchTimeSeriesData
  };
};

// Legacy hook for backwards compatibility
export const useTimeSeriesStats = (language: string = 'es') => {
  const userId = useSafeUserId();
  const [cache, setCache] = useState<CachedData<TimeSeriesStats>>({
    data: null,
    timestamp: 0,
    loading: false,
    error: null
  });

  // Performance optimized memoized cache check
  const isCacheValid = useMemo(() => {
    return cache.data &&
           cache.timestamp &&
           (Date.now() - cache.timestamp) < CACHE_DURATION;
  }, [cache.timestamp]);

  // Fetch dashboard statistics with performance monitoring
  const fetchStats = useCallback(async (forceRefresh: boolean = false) => {
    if (!userId) return;

    // Use cache if valid and not forcing refresh
    if (isCacheValid && !forceRefresh) {
      return cache.data;
    }

    setCache(prev => ({ ...prev, loading: true, error: null }));
    const startTime = performance.now();

    try {
      const { data, error } = await supabase
        .rpc('get_dashboard_statistics', {
          p_user_id: userId,
          p_language: language
        });

      const duration = performance.now() - startTime;

      // Performance monitoring - log if query is slow
      if (duration > 200) {
        console.warn(`Dashboard stats query took ${duration.toFixed(2)}ms - consider optimization`);
      }

      if (error) throw error;

      const statsData = data as TimeSeriesStats;

      setCache({
        data: statsData,
        timestamp: Date.now(),
        loading: false,
        error: null
      });

      return statsData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setCache(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));

      console.error('Failed to fetch dashboard stats:', error);
      return null;
    }
  }, [userId, language, isCacheValid, cache.data]);

  // Auto-refresh on component mount and user/language change
  useEffect(() => {
    if (userId && language) {
      fetchStats();
    }
  }, [userId, language, fetchStats]);

  // Real-time subscription for SRS card updates (invalidates cache)
  useEffect(() => {
    if (!userId) return;

    const subscription = supabase
      .channel(`srs_updates_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'srs_cards_minimal',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Debounce multiple rapid updates
          setTimeout(() => {
            fetchStats(true); // Force refresh after SRS updates
          }, REALTIME_UPDATE_THRESHOLD);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'study_activity_log',
          filter: `user_id=eq.${userId}`
        },
        () => {
          // Study activity updates should refresh streak data
          setTimeout(() => {
            fetchStats(true);
          }, REALTIME_UPDATE_THRESHOLD);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId, fetchStats]);

  // Memoized derived data for charts
  const chartData = useMemo(() => {
    if (!cache.data) return null;

    return {
      // Study streak chart data
      streakData: {
        current: cache.data.study_streak.current,
        target: Math.max(cache.data.study_streak.longest, 7), // Show progress toward longest or 7-day goal
        percentage: cache.data.study_streak.longest > 0
          ? (cache.data.study_streak.current / cache.data.study_streak.longest) * 100
          : 0
      },

      // Words learned growth chart
      growthData: {
        labels: ['7 Days', '30 Days'],
        datasets: [{
          label: 'Words Learned',
          data: [
            cache.data.growth['7d'].words_learned,
            cache.data.growth['30d'].words_learned
          ],
          backgroundColor: ['#10B981', '#3B82F6']
        }]
      },

      // Total words breakdown (pie chart)
      wordsBreakdown: {
        labels: ['Mastered', 'Learning', 'Due for Review'],
        datasets: [{
          data: [
            cache.data.total_words.mastered,
            cache.data.total_words.learning - cache.data.total_words.due_for_review,
            cache.data.total_words.due_for_review
          ],
          backgroundColor: ['#10B981', '#F59E0B', '#EF4444']
        }]
      }
    };
  }, [cache.data]);

  // Performance metrics for monitoring
  const performanceMetrics = useMemo(() => ({
    cacheHitRate: isCacheValid ? 100 : 0,
    lastFetchTime: cache.timestamp,
    cacheAge: cache.timestamp ? Date.now() - cache.timestamp : 0,
    isOptimized: Boolean(cache.data && isCacheValid)
  }), [cache.timestamp, isCacheValid, cache.data]);

  return {
    // Main data
    stats: cache.data,
    chartData,

    // State
    loading: cache.loading,
    error: cache.error,

    // Actions
    refresh: () => fetchStats(true),

    // Performance monitoring
    performanceMetrics,

    // Cache status (useful for debugging)
    cacheStatus: {
      isValid: isCacheValid,
      age: cache.timestamp ? Date.now() - cache.timestamp : 0,
      nextRefresh: cache.timestamp + CACHE_DURATION
    }
  };
};